import { Box, CircularProgress, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import React, { useEffect, useMemo, useState } from 'react';
import useBundleTranslation from 'i18n';
import parse from 'html-react-parser';
import MarkdownIt from 'markdown-it';
import { SearchResults } from 'app/concierge/types';
import ResultItems from '../result-items';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setWaitingConcierge } from 'store/globalSearchSlice';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';
import { conciergeAPI } from 'api/concierge';
import { parseMessage, stripHtmlTags } from 'app/concierge/tools';
import { dispatchCustomEvent } from 'tools/events';

const md = MarkdownIt();

interface JsonSearchResultsProps {
    searchResult: SearchResults;
    onShowInSearch: (params: any, data: any) => void;
    setSelectedMessage: (index: number | null) => void;
    setSummaryOnly: (value: boolean) => void;
    showSearchRequestIcon?: boolean;
    getSearchParams?: (params: any) => React.ReactNode;
    onSearchPage?: boolean;
    loadMore?: boolean;
    onUpdateSearchResult?: (updatedResult: SearchResults) => void;
    messageId?: string;
    useInfoButton?: boolean;
}

export default function JsonSearchResults({
    searchResult,
    onShowInSearch,
    setSelectedMessage,
    setSummaryOnly,
    showSearchRequestIcon,
    getSearchParams,
    onSearchPage,
    loadMore,
    onUpdateSearchResult,
    messageId,
    useInfoButton,
}: JsonSearchResultsProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();
    const location = useLocation();
    const useConciergeForSearch = useConfigConstantValue('USE_CONCIERGE_FOR_GLOBAL_SEARCH');
    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const goStraightToSearch = conciergeSettings.number_of_results_in_concierge_panel === 0;
    const quantity = searchResult.quantity ?? 0;
    const data = searchResult.data ?? [];
    const moreCount = quantity - data.length;
    const fromSearch = useSelector((state: any) => state.concierge.fromSearch) as boolean;
    const isAppPanel = useSelector((state: any) => state.concierge.isAppPanel) as boolean;

    const [loadMoreParams, setLoadMoreParams] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);

    const searchResultId = useMemo(() => {
        return `${searchResult.query}_${quantity}_${data.length}`;
    }, [searchResult.query, quantity, data.length]);

    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.activeChatMessage) as typeof defaultStyles;
    }, [customStyles]);

    useEffect(() => {
        processShowMoreURI(searchResult.show_more_uri);
    }, [searchResult.show_more_uri]);

    const processShowMoreURI = (showMoreUrl: string) => {
        if (showMoreUrl) {
            try {
                const url = new URL(showMoreUrl, window.location.origin);
                const params = url.search;
                setLoadMoreParams(params);
            } catch (error) {
                console.error('Error parsing show_more_uri:', error);
                setLoadMoreParams('');
            }
        } else {
            setLoadMoreParams('');
        }
    };

    const onLoadMore = () => {
        setLoadingMore(true);
        conciergeAPI.loadMore(loadMoreParams).then(async (response) => {
            const stream: ReadableStream = response.data;
            const reader = stream.getReader();
            const decoder = new TextDecoder();
            let message = '';

            while (true) {
                const { done, value } = await reader.read();

                if (value) {
                    const textChunk = decoder.decode(value, { stream: true });
                    if (textChunk) {
                        message += textChunk;
                    }
                }

                if (done) {
                    const finalText = decoder.decode();
                    if (finalText) {
                        message += finalText;
                    }
                    break;
                }
            }

            parseLoadMore(message);
        });
    };

    const parseLoadMore = (message: string) => {
        const parsedMessageParts = parseMessage(message, Date.now().toString());

        if (parsedMessageParts.length > 0) {
            parsedMessageParts.forEach((part) => {
                if (part.type === 'json' && part.jsonBlocks) {
                    part.jsonBlocks.forEach((block) => {
                        if (block.data_type === 'search_results') {
                            const loadMoreData = block as SearchResults;

                            if (onUpdateSearchResult) {
                                const updatedSearchResult = {
                                    ...searchResult,
                                    data: [...data, ...(loadMoreData.data ?? [])],
                                    show_more_uri: loadMoreData.show_more_uri,
                                };

                                onUpdateSearchResult(updatedSearchResult);
                            }
                            processShowMoreURI(loadMoreData.show_more_uri);
                        }
                    });
                }
            });
        }

        setLoadingMore(false);
    };

    useEffect(() => {
        const redirectKey = messageId ? `concierge_redirected_${messageId}` : `concierge_redirected_${searchResultId}`;
        const wasRedirected = sessionStorage.getItem(redirectKey);

        if (!wasRedirected && (onSearchPage || goStraightToSearch || (useConciergeForSearch && fromSearch))) {
            sessionStorage.setItem(redirectKey, 'true');
            setSelectedMessage(null);
            if (quantity > 0) {
                onShowInSearch(searchResult.params, data);
            }
            dispatch(setWaitingConcierge(false));
            setSummaryOnly(true);
        }
    }, [messageId, searchResultId, onSearchPage, goStraightToSearch, useConciergeForSearch, fromSearch, quantity]);

    const showInSearch = data.length > 0 && !loadMore;
    const showLoadMore = data.length > 0 && loadMore;

    return (
        <Stack spacing={1} sx={{ mb: 1 }}>
            <Box sx={styles.dialogItemText}>
                {parse(md.render(stripHtmlTags(searchResult.search_criteria_summary_phrase)))}
            </Box>
            <ResultItems items={data} useInfoButton={useInfoButton} />
            {showInSearch && (
                <Stack
                    direction={'row'}
                    sx={styles.showInSearch}
                    onClick={() => {
                        if (isAppPanel) {
                            dispatchCustomEvent('mi-concierge-panel', {
                                action: 'show-in-search',
                                payload: {
                                    searchParams: searchResult.params,
                                },
                            });
                        } else {
                            onShowInSearch(searchResult.params, data);
                            setSelectedMessage(null);
                        }
                    }}
                    spacing={1}
                >
                    <Box sx={styles.showInSearchIcon}>
                        <IconMi icon="arrow-left" sx={{ fontSize: '16px' }} />
                    </Box>
                    <Box>
                        {moreCount > 1 ? t('message.view_more', { count: moreCount }) : t('message.show_in_search')}
                    </Box>
                </Stack>
            )}
            {showLoadMore && (
                <Stack
                    direction={'row'}
                    sx={styles.showInSearch}
                    className={loadingMore ? 'loading' : ''}
                    onClick={() => {
                        if (loadingMore) return;
                        onLoadMore();
                        setSelectedMessage(null);
                    }}
                    spacing={1}
                >
                    <Box>{t('message.load_more')}</Box>
                    <Box sx={styles.showInSearchIcon}>
                        {loadingMore ? <CircularProgress size={'16px'} color={'inherit'} /> : null}
                    </Box>
                </Stack>
            )}
        </Stack>
    );
}
